import React, { useEffect, useMemo, useState } from "react";
import { FastField, getIn } from "formik";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
  KeyboardDateTimePicker,
  KeyboardTimePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import viLocale from "date-fns/locale/vi";
import ErrorIcon from "./ErrorIcon";
import configDefaultForm from "./configDefault";
import RequiredLabel from "./RequiredLabel";
import moment from "moment";

const GlobitsDateTimePicker = (props) => (
  <FastField
    {...props}
    name={props.name}
    shouldUpdate={shouldComponentUpdate}
  >
    {({ field, meta, form }) => {
      return (
        <Component
          {...props}
          field={field}
          meta={meta}
          setFieldValue={form.setFieldValue}
        />
      );
    }}
  </FastField>
);

const Component = ({
  disabled,
  fullWidth = configDefaultForm.fullWidth,
  label,
  name,
  size = configDefaultForm.size,
  inputVariant = configDefaultForm.inputVariant,
  className = "",
  debounceTime = configDefaultForm.debounceTime,
  notDelay,
  field,
  meta,
  requiredLabel,
  required,
  onChange,
  readOnly,
  InputProps,
  InputLabelProps,
  disablePast = false,
  disableFuture = false,
  isDateTimePicker,
  isTimePicker,
  isDatePicker, 
  format = isTimePicker ?  " HH:mm" : ( isDatePicker ? "dd/MM" : "dd/MM/yyyy" + (isDateTimePicker ? " HH:mm" : '')),
  minDate,
  maxDate,
  minDateMessage = "Ngày không hợp lệ",
  maxDateMessage = "Ngày không hợp lệ",
  okLabel = "CHỌN",
  cancelLabel = "HUỶ",
  setFieldValue,
  multiple,
  notValueMillisecond = configDefaultForm.datePicker.notValueMillisecond,
  tabIndex,
  ...otherProps
}) => {
  const [value, setValue] = useState(field.value);
  const [t, setT] = useState(undefined);

  useEffect(() => {
    setValue(field.value ?? null);
  }, [field.value])

  const handleChange = (value) => {
    setValue(value);
    let newDate = value;
    if (!notValueMillisecond && moment(newDate, "DD/MM/YYYY", true).isValid()) {
      newDate = new Date(newDate).getTime();
    }

    if (!notDelay) {
      if (t) {
        clearTimeout(t);
      }

      setT(setTimeout(() => {
        if (onChange) {
          onChange(newDate);
        } else {
          setFieldValue(name, newDate);
        }
      }, debounceTime));
    } else {
      if (onChange) {
        onChange(newDate);
      } else {
        setFieldValue(name, newDate);
      }
    }
  }

  const displayLabel = useMemo(() => label ? <RequiredLabel label={label} requiredLabel={required || requiredLabel} /> : null, [label, required, requiredLabel]);
  const isError = meta?.touched && meta?.error

  const configDate = {
    ...otherProps,
    ...field,
    name: name,
    id: name,
    disabled: disabled,
    fullWidth: fullWidth,
    label: (fullWidth && displayLabel) ? displayLabel : null,
    size: size,
    inputVariant: inputVariant,
    value: value || null,
    onChange: handleChange,
    minDate,
    maxDate,
    format: format,
    error: isError,
    helperText: (fullWidth && isError) ? meta.error : "",
    InputProps: {
      readOnly: readOnly,
      tabIndex: tabIndex || 0,
      ...InputProps,
    },
    inputProps: {
      tabIndex: tabIndex
    },
    KeyboardButtonProps: {
      tabIndex: tabIndex || 0, // Thiết lập tabIndex cho icon button
    },
    InputLabelProps: {
      htmlFor: name,
      ...InputLabelProps,
    },
    readOnly: readOnly,
    className: `input-container ${className} ${readOnly ? 'read-only' : ''} date-picker-input`,
    disablePast: disablePast,
    disableFuture: disableFuture,
    invalidDateMessage: "Ngày không hợp lệ",
    minDateMessage: minDateMessage,
    maxDateMessage: maxDateMessage,
    okLabel: okLabel,
    cancelLabel: cancelLabel
  }
  return (
    <>
      {(!fullWidth && displayLabel) &&
        <label htmlFor={name}>
          {displayLabel}
        </label>
      }
      <MuiPickersUtilsProvider utils={DateFnsUtils} locale={viLocale}>
        {isDateTimePicker ?
          <KeyboardDateTimePicker {...configDate} /> :
          (isTimePicker ? <KeyboardTimePicker {...configDate} /> : <KeyboardDatePicker {...configDate} />)}
      </MuiPickersUtilsProvider>
      {(isError && !fullWidth) && <ErrorIcon helperText={meta.error} />}
    </>
  )
};

const shouldComponentUpdate = (nextProps, currentProps) => {
  return (
    nextProps.name !== currentProps.name ||
    nextProps.value !== currentProps.value ||
    nextProps.onChange !== currentProps.onChange ||
    nextProps.disablePast !== currentProps.disablePast ||
    nextProps.disableFuture !== currentProps.disableFuture ||
    nextProps.label !== currentProps.label ||
    nextProps.required !== currentProps.required ||
    nextProps.disabled !== currentProps.disabled ||
    nextProps.readOnly !== currentProps.readOnly ||
    nextProps.formik.isSubmitting !== currentProps.formik.isSubmitting ||
    Object.keys(nextProps).length !== Object.keys(currentProps).length ||
    getIn(nextProps.formik.values, currentProps.name) !== getIn(currentProps.formik.values, currentProps.name) ||
    getIn(nextProps.formik.errors, currentProps.name) !== getIn(currentProps.formik.errors, currentProps.name) ||
    getIn(nextProps.formik.touched, currentProps.name) !== getIn(currentProps.formik.touched, currentProps.name)
  );
};

export default React.memo(GlobitsDateTimePicker);
